import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import {
  getApiGatewayUrl,
  getApiUrl,
  getApiV2Url,
  getAuthUrl,
  getUserApiUrl,
} from '../constants/apiUrlConstants'
import { ApiErrorResponse } from '../types'
import { apiErrorHandler } from '../utils/api.utils'
import {
  createAuthResponseInterceptor,
  createAxiosInstance,
  createManagedRequestInterceptor,
  createPublicRequestInterceptor,
  type ApiTokenType,
} from '../utils/axios.utils'

export type ApiRequestConfig = AxiosRequestConfig & {
  authTokenType?: ApiTokenType | false
  headers?: AxiosRequestConfig['headers'] & {
    authTokenType?: ApiTokenType | false
    skipToast?: boolean | string
  }
}

const request = createAxiosInstance()
const requestV2 = createAxiosInstance()
const publicRequest = createAxiosInstance()
const userRequest = createAxiosInstance()

const apiGatewayRequest = createAxiosInstance()

const authRequest = createAxiosInstance()

request.interceptors.request.use(createManagedRequestInterceptor(getApiUrl, 'accessToken'))
requestV2.interceptors.request.use(createManagedRequestInterceptor(getApiV2Url, 'accessToken'))
userRequest.interceptors.request.use(createManagedRequestInterceptor(getUserApiUrl, 'accessToken'))
apiGatewayRequest.interceptors.request.use(createManagedRequestInterceptor(getApiGatewayUrl, 'accessToken'))
publicRequest.interceptors.request.use(createPublicRequestInterceptor(getApiUrl))
authRequest.interceptors.request.use(createPublicRequestInterceptor(getAuthUrl))

request.interceptors.response.use((response: AxiosResponse) => response, createAuthResponseInterceptor(request))
requestV2.interceptors.response.use(
  (response: AxiosResponse) => response,
  createAuthResponseInterceptor(requestV2),
)
userRequest.interceptors.response.use(
  (response: AxiosResponse) => response,
  createAuthResponseInterceptor(userRequest),
)
apiGatewayRequest.interceptors.response.use(
  (response: AxiosResponse) => response,
  createAuthResponseInterceptor(apiGatewayRequest),
)
publicRequest.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiErrorResponse>) => Promise.reject(apiErrorHandler(error)),
)

authRequest.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<ApiErrorResponse>) => Promise.reject(apiErrorHandler(error)),
)

export {
  apiGatewayRequest as apiGatewayAxiosInstance,
  authRequest as authAxiosInstance,
  request as axiosInstance,
  requestV2 as axiosV2Instance,
  publicRequest as publicAxiosInstance,
  userRequest as userAxiosInstance,
}
